import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ConfigProvider, message, Modal, Select, Spin, Tooltip } from 'antd';
import { BarChartOutlined, DeleteOutlined, FileExcelOutlined, FileTextOutlined, PlusOutlined, WarningFilled } from '@ant-design/icons';
import { usePartnershipContext } from '../../../core/context/PartnershipContext';
import { calcProfileService, CalcProfile as CalcProfileData, CalcProfileItem } from './CalcProfileService';
import { standardCalcService, StandardCalculation } from './StandardCalcService';
import { TemplateService, TemplateVersion } from '../Templates/TemplateService';
import { MODAL_STYLES, MODAL_TITLE_STYLE, darkFormTheme } from '../../../styles/modalStyles';
import styles from './CalcProfile.module.css';

// ── Category colours (mirrors Templates screen) ────────────────────────────────

const SC_CATEGORY_COLORS: Record<string, { bg: string; color: string }> = {
  Tax:          { bg: 'rgba(37,99,235,0.15)',   color: '#60a5fa' },
  Allocation:   { bg: 'rgba(242,101,34,0.15)',  color: '#F26522' },
  Capital:      { bg: 'rgba(124,58,237,0.15)',  color: '#a78bfa' },
  Depreciation: { bg: 'rgba(20,184,166,0.15)',  color: '#2dd4bf' },
  Output:       { bg: 'rgba(34,197,94,0.15)',   color: '#22c55e' },
  Event:        { bg: 'rgba(255,255,255,0.07)', color: '#9ca3af' },
};
const UNTAGGED_COLORS = { bg: 'rgba(255,255,255,0.06)', color: '#6b7280' };

// ── Badge helpers ──────────────────────────────────────────────────────────────

function CategoryBadge({ category }: { category?: string | null }) {
  if (!category) return null;
  const { bg, color } = SC_CATEGORY_COLORS[category] ?? UNTAGGED_COLORS;
  return (
    <span style={{
      background: bg, color, padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {category}
    </span>
  );
}

function TypeBadge({ type }: { type?: string | null }) {
  const isPeriod = type !== 'Event';
  return (
    <span className={isPeriod ? styles.badgePeriod : styles.badgeEvent}>
      {isPeriod ? 'Period' : 'Event'}
    </span>
  );
}

// ── Dependency warning ─────────────────────────────────────────────────────────

function DepWarning({
  scCode,
  allSCs,
  profileSCCodes,
}: {
  scCode: string;
  allSCs: StandardCalculation[];
  profileSCCodes: Set<string>;
}) {
  const sc = allSCs.find(s => s.code === scCode);
  if (!sc?.dependencies) return null;
  const depCodes = sc.dependencies.split(',').map(d => d.trim()).filter(Boolean);
  const missing = depCodes.filter(d => !profileSCCodes.has(d));
  if (missing.length === 0) return null;
  const names = missing
    .map(code => allSCs.find(s => s.code === code)?.name ?? code)
    .join(', ');
  return (
    <Tooltip title={`Depends on ${names} — add it to the profile`}>
      <WarningFilled style={{ color: '#fbbf24', fontSize: 14, cursor: 'help' }} />
    </Tooltip>
  );
}

// ── Assign Template Modal (file upload for existing items) ─────────────────────

const AssignTemplateModal: React.FC<{
  item: CalcProfileItem;
  partnershipName: string;
  onClose: () => void;
  onSaved: (fileName: string) => void;
}> = ({ item, partnershipName, onClose, onSaved }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const accept = (f: File) => {
    if (!f.name.endsWith('.xlsx')) { message.error('Only .xlsx files are accepted'); return; }
    if (f.size > 50 * 1024 * 1024) { message.error('File exceeds 50 MB limit'); return; }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await calcProfileService.assignTemplate(item.id, fd);
      message.success(`Template assigned: ${file.name}`);
      onSaved(file.name);
    } catch {
      message.error('Failed to assign template');
      setUploading(false);
    }
  };

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={<span style={MODAL_TITLE_STYLE}>Assign Template</span>}
      centered
      width={520}
      styles={MODAL_STYLES}
    >
      <p style={{ fontSize: 12, color: 'var(--ce-text-muted)', margin: '0 0 20px' }}>
        {item.standardCalculationName} · {partnershipName}
      </p>

      <div
        className={`${styles.dropZone}${dragOver ? ' ' + styles.dropZoneActive : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) accept(f); }}
        onClick={() => inputRef.current?.click()}
      >
        <FileExcelOutlined style={{ fontSize: 26, color: '#F26522' }} />
        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--ce-text-primary)', margin: '8px 0 4px' }}>
          {file ? file.name : 'Drag & drop or click to upload'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ce-text-muted)' }}>
          {file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : '.xlsx files only · max 50 MB'}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx"
          style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; if (f) accept(f); }}
        />
      </div>

      {file && (
        <div className={styles.dropZoneFile}>✓ {file.name} ready to upload</div>
      )}

      <div className={styles.formActions}>
        <button className={styles.btnOutline} onClick={onClose} disabled={uploading}>Cancel</button>
        <button className={styles.btnPrimary} onClick={handleUpload} disabled={!file || uploading}>
          {uploading ? 'Uploading…' : 'Upload & Assign'}
        </button>
      </div>
    </Modal>
  );
};

// ── Calculation Profile Tab ────────────────────────────────────────────────────

const CalcProfileTab: React.FC<{
  profile: CalcProfileData | null;
  allSCs: StandardCalculation[];
  onDeleteItem: (item: CalcProfileItem) => void;
  onAssignTemplate: (item: CalcProfileItem) => void;
}> = ({ profile, allSCs, onDeleteItem, onAssignTemplate }) => {
  const [subTab, setSubTab] = useState<'Period' | 'Event'>('Period');

  const items = profile?.items ?? [];
  const profileSCCodes = new Set(items.map(it => it.standardCalculationId));

  const filtered = items.filter(it =>
    subTab === 'Event' ? it.type === 'Event' : it.type !== 'Event',
  );

  const periodCount = items.filter(it => it.type !== 'Event').length;
  const eventCount  = items.filter(it => it.type === 'Event').length;

  return (
    <div>
      <div className={styles.subTabRow}>
        <button
          className={subTab === 'Period' ? styles.subTabActive : styles.subTab}
          onClick={() => setSubTab('Period')}
        >
          Period
        </button>
        <button
          className={subTab === 'Event' ? styles.subTabActive : styles.subTab}
          onClick={() => setSubTab('Event')}
        >
          Event
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><FileTextOutlined style={{ fontSize: 32, color: '#ADADAD' }} /></div>
          <div className={styles.emptyTitle}>No {subTab.toLowerCase()} calculations in this profile.</div>
          <div className={styles.emptyText}>Click + Add Calculation to get started.</div>
        </div>
      ) : (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Standard Calculation</th>
                <th>Category</th>
                <th>Type</th>
                <th>Template</th>
                <th style={{ width: 36 }} />
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => {
                const hasTemplate = !!item.templateName || !!item.templatePath;
                const templateLabel = item.templateName
                  ? `${item.templateName}${item.versionOverride ? ' ' + item.versionOverride : ''}`
                  : null;

                return (
                  <tr key={item.id} className={styles.tableRow}>
                    <td>
                      <span className={styles.cellName}>{item.standardCalculationName}</span>
                    </td>
                    <td>
                      <CategoryBadge category={item.category} />
                    </td>
                    <td>
                      <TypeBadge type={item.type} />
                    </td>
                    <td>
                      {hasTemplate ? (
                        <span className={styles.templateCell}>
                          <FileExcelOutlined style={{ fontSize: 12 }} />
                          {templateLabel}
                        </span>
                      ) : (
                        <span
                          className={styles.templateCellEmpty}
                          onClick={() => onAssignTemplate(item)}
                        >
                          No template assigned
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <DepWarning
                        scCode={item.standardCalculationId}
                        allSCs={allSCs}
                        profileSCCodes={profileSCCodes}
                      />
                    </td>
                    <td>
                      <button
                        className={styles.removeBtn}
                        title={`Remove ${item.standardCalculationName}`}
                        onClick={() => onDeleteItem(item)}
                      >
                        <DeleteOutlined />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className={styles.tableFooter}>
            Showing {filtered.length} {subTab.toLowerCase()} calculation{filtered.length !== 1 ? 's' : ''} · {periodCount + eventCount} total in profile
          </div>
        </>
      )}
    </div>
  );
};

// ── Add Calculation Modal (2-step) ─────────────────────────────────────────────

const AddCalcModal: React.FC<{
  profile: CalcProfileData | null;
  partnershipId: string;
  taxYear: string;
  onClose: () => void;
  onSaved: (autoAddedNames: string[]) => void;
}> = ({ profile, partnershipId, taxYear, onClose, onSaved }) => {
  const [step, setStep]   = useState<1 | 2>(1);
  const [allCalcs, setAllCalcs]   = useState<StandardCalculation[]>([]);
  const [templates, setTemplates] = useState<TemplateVersion[]>([]);
  const [selected, setSelected]   = useState<Set<string>>(new Set()); // SC codes
  const [templateSelections, setTemplateSelections] = useState<Record<string, number | null>>({}); // SC code → TemplateVersion.id
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    Promise.all([
      standardCalcService.getAll(),
      TemplateService.getVersions().catch(() => [] as TemplateVersion[]),
    ])
      .then(([calcs, tvs]) => {
        setAllCalcs(calcs);
        const tvArr = Array.isArray(tvs) ? tvs : [];
        setTemplates(tvArr);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const existingCodes = new Set((profile?.items ?? []).map(it => it.standardCalculationId));
  const available = allCalcs.filter(c => c.isActive && !existingCodes.has(c.code));

  const periodCalcs = available.filter(c => c.periodType !== 'Event').sort((a, b) => a.sortOrder - b.sortOrder);
  const eventCalcs  = available.filter(c => c.periodType === 'Event').sort((a, b) => a.sortOrder - b.sortOrder);

  const selectedCalcs = allCalcs.filter(c => selected.has(c.code));

  const profileSCCodes = existingCodes;

  const toggle = (code: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });

  const handleNext = () => {
    if (selected.size === 0) return;
    setStep(2);
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      const autoAddedNames = new Set<string>();
      for (const sc of selectedCalcs.sort((a, b) => a.sortOrder - b.sortOrder)) {
        const tvId = templateSelections[sc.code] ?? null;
        const tv   = tvId != null ? templates.find(t => t.id === tvId) : null;
        const result = await calcProfileService.addItem(partnershipId, taxYear, {
          standardCalculationId: sc.code,
          templateId: null,
          versionOverride: tv ? `v${tv.versionNumber}` : null,
          templateName: tv?.fileName ?? null,
          templatePath: tv?.blobPath ?? null,
        });
        result.autoAddedItemNames.forEach(name => autoAddedNames.add(name));
      }
      message.success(`${selectedCalcs.length} calculation${selectedCalcs.length !== 1 ? 's' : ''} added to profile`);
      onSaved(Array.from(autoAddedNames));
    } catch {
      message.error('Failed to add calculations');
    } finally {
      setSaving(false);
    }
  };

  const renderCalcGroup = (calcs: StandardCalculation[], label: string) => {
    if (calcs.length === 0) return null;
    return (
      <div className={styles.calcGroup}>
        <div className={styles.calcGroupLabel}>{label}</div>
        {calcs.map(c => {
          const depCodes = c.dependencies?.split(',').map(d => d.trim()).filter(Boolean) ?? [];
          const missingDeps = depCodes.filter(d => !profileSCCodes.has(d) && !selected.has(d));
          const isChecked = selected.has(c.code);
          return (
            <div
              key={c.id}
              className={`${styles.calcCheckRow} ${isChecked ? styles.calcCheckRowSelected : ''}`}
              onClick={() => toggle(c.code)}
            >
              <input
                type="checkbox"
                readOnly
                checked={isChecked}
                style={{ width: 15, height: 15, accentColor: 'var(--ce-accent)', cursor: 'pointer', flexShrink: 0 }}
              />
              <span className={styles.calcCheckName}>{c.name}</span>
              <CategoryBadge category={c.category} />
              <span className={c.periodType !== 'Event' ? styles.badgePeriod : styles.badgeEvent}>
                {c.periodType !== 'Event' ? 'Period' : 'Event'}
              </span>
              {missingDeps.length > 0 && (
                <Tooltip title={`Depends on ${missingDeps.map(d => allCalcs.find(s => s.code === d)?.name ?? d).join(', ')} — also add it`}>
                  <WarningFilled style={{ color: '#fbbf24', fontSize: 13 }} />
                </Tooltip>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderStep1 = () => (
    <>
      <div className={styles.stepHeader}>
        <div className={`${styles.stepDot} ${styles.stepDotActive}`}>1</div>
        <span className={styles.stepLabel}>Select calculations</span>
        <div className={styles.stepConnector} />
        <div className={styles.stepDot}>2</div>
        <span className={styles.stepLabelMuted}>Assign templates</span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}><Spin /></div>
      ) : available.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--ce-text-muted)', fontSize: 13 }}>
          All standard calculations are already in this profile.
        </div>
      ) : (
        <div className={styles.formStack} style={{ maxHeight: '55vh', overflowY: 'auto', paddingRight: 4 }}>
          {renderCalcGroup(periodCalcs, 'Period Calculations')}
          {renderCalcGroup(eventCalcs, 'Event Calculations')}
        </div>
      )}

      <div className={styles.formActions}>
        <button className={styles.btnOutline} onClick={onClose}>Cancel</button>
        <button
          className={styles.btnPrimary}
          onClick={handleNext}
          disabled={selected.size === 0}
        >
          Next — Assign Templates
        </button>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <div className={styles.stepHeader}>
        <div className={styles.stepDot}>1</div>
        <span className={styles.stepLabelMuted}>Select calculations</span>
        <div className={styles.stepConnector} />
        <div className={`${styles.stepDot} ${styles.stepDotActive}`}>2</div>
        <span className={styles.stepLabel}>Assign templates</span>
      </div>

      <p style={{ fontSize: 12, color: 'var(--ce-text-muted)', margin: '0 0 16px' }}>
        Template selection is optional — you can assign templates later.
      </p>

      <ConfigProvider theme={darkFormTheme}>
        <div className={styles.formStack} style={{ maxHeight: '55vh', overflowY: 'auto', paddingRight: 4 }}>
          {selectedCalcs.map(sc => {
            const tvForSC = templates.filter(t => t.standardCalculationCode === sc.code);
            const activeFirst = [...tvForSC].sort((a, b) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0));
            // When no templates are tagged to this SC, show all templates (including untagged)
            const fallbackAll = tvForSC.length === 0;
            const displayTemplates = fallbackAll
              ? [...templates].sort((a, b) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0))
              : activeFirst;
            return (
              <div key={sc.code} className={styles.templatePickerRow}>
                <div className={styles.templatePickerLabel}>
                  <span className={styles.cellName}>{sc.name}</span>
                  <CategoryBadge category={sc.category} />
                </div>
                {displayTemplates.length === 0 ? (
                  <Select
                    disabled
                    style={{ width: '100%' }}
                    placeholder="No templates available — upload one first"
                  />
                ) : (
                  <Select
                    allowClear
                    style={{ width: '100%' }}
                    placeholder={fallbackAll ? 'All templates (none tagged to this SC)' : 'No template assigned (optional)'}
                    value={templateSelections[sc.code] ?? null}
                    onChange={val => setTemplateSelections(prev => ({ ...prev, [sc.code]: val ?? null }))}
                    options={displayTemplates.map(t => ({
                      value: t.id,
                      label: `${t.fileName} v${t.versionNumber}${t.isActive ? ' (active)' : ''}${fallbackAll && !t.standardCalculationCode ? ' (untagged)' : ''}`,
                    }))}
                  />
                )}
              </div>
            );
          })}
        </div>
      </ConfigProvider>

      <div className={styles.formActions}>
        <button className={styles.btnOutline} onClick={() => setStep(1)} disabled={saving}>Back</button>
        <button className={styles.btnPrimary} onClick={handleAdd} disabled={saving}>
          {saving ? 'Adding…' : `Add ${selectedCalcs.length} Calculation${selectedCalcs.length !== 1 ? 's' : ''} to Profile`}
        </button>
      </div>
    </>
  );

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={<span style={MODAL_TITLE_STYLE}>Add Calculation to Profile</span>}
      centered
      width={600}
      styles={MODAL_STYLES}
    >
      {step === 1 ? renderStep1() : renderStep2()}
    </Modal>
  );
};

// ── Sequencing Tab ─────────────────────────────────────────────────────────────

function topoSort(items: CalcProfileItem[], scByCode: Map<string, StandardCalculation>): CalcProfileItem[] {
  const result: CalcProfileItem[] = [];
  const remaining = [...items];
  const inResult = new Set<string>();

  while (remaining.length > 0) {
    const before = remaining.length;
    for (let i = remaining.length - 1; i >= 0; i--) {
      const item = remaining[i];
      const sc = scByCode.get(item.standardCalculationId);
      const depCodes = sc?.dependencies?.split(',').map(d => d.trim()).filter(Boolean) ?? [];
      const satisfied = depCodes.every(d => inResult.has(d));
      if (satisfied) {
        result.push(item);
        inResult.add(item.standardCalculationId);
        remaining.splice(i, 1);
      }
    }
    if (remaining.length === before) {
      result.push(...remaining);
      break;
    }
  }
  return result;
}

const SequencingTab: React.FC<{
  profile: CalcProfileData | null;
  allSCs: StandardCalculation[];
}> = ({ profile, allSCs }) => {
  const items = profile?.items ?? [];
  const scByCode = new Map(allSCs.map(s => [s.code, s]));

  if (items.length === 0) {
    return (
      <div style={{ padding: '0 24px 24px' }}>
        <div className={styles.emptyState}>
          <div className={styles.emptyTitle}>No calculations in profile.</div>
          <div className={styles.emptyText}>Add calculations first to see the execution order.</div>
        </div>
      </div>
    );
  }

  const periodItems = items.filter(it => it.type !== 'Event');
  const eventItems  = items.filter(it => it.type === 'Event');

  const sortedPeriod = topoSort(periodItems, scByCode);
  const sortedEvent  = topoSort(eventItems, scByCode);

  const renderSection = (arr: CalcProfileItem[], label: string) => {
    if (arr.length === 0) return null;
    return (
      <div className={styles.seqSection}>
        <div className={styles.seqSectionLabel}>{label}</div>
        {arr.map((item, idx) => {
          const sc = scByCode.get(item.standardCalculationId);
          const depCodes = sc?.dependencies?.split(',').map(d => d.trim()).filter(Boolean) ?? [];
          const depNames = depCodes.map(code => allSCs.find(s => s.code === code)?.name ?? code);
          return (
            <div key={item.id} className={styles.seqItem}>
              <div className={styles.seqNum}>{idx + 1}</div>
              <div style={{ flex: 1 }}>
                <div className={styles.seqName}>{item.standardCalculationName}</div>
                <span className={styles.seqDep}>
                  {depNames.length === 0
                    ? 'no upstream dependency — can run first'
                    : <>must run after{' '}
                        {depNames.map((n, i) => (
                          <React.Fragment key={n}>
                            <span className={styles.seqDepLink}>{n}</span>
                            {i < depNames.length - 1 ? ', ' : ''}
                          </React.Fragment>
                        ))}
                      </>}
                </span>
              </div>
              <TypeBadge type={item.type} />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ padding: '0 24px 24px' }}>
      <p style={{ fontSize: 13, color: 'var(--ce-text-muted)', margin: '0 0 20px' }}>
        Execution order is set automatically based on declared dependencies. Adding a calculation will auto-include any missing dependencies.
      </p>
      {renderSection(sortedPeriod, 'Period Calculations')}
      {renderSection(sortedEvent, 'Event Calculations')}
    </div>
  );
};

// ── CalcProfile ────────────────────────────────────────────────────────────────

export const CalcProfile: React.FC = () => {
  const { selectedPartnership, taxYear } = usePartnershipContext();

  const [profile, setProfile]     = useState<CalcProfileData | null>(null);
  const [allSCs, setAllSCs]       = useState<StandardCalculation[]>([]);
  const [loading, setLoading]     = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'sequencing'>('profile');

  const [showAdd, setShowAdd]         = useState(false);
  const [assignItem, setAssignItem]   = useState<CalcProfileItem | null>(null);
  const [autoAddedBanner, setAutoAddedBanner] = useState<string[] | null>(null);

  const fetchProfile = useCallback(async (partnershipId: string, year: string) => {
    setLoading(true);
    try {
      const [data, scs] = await Promise.all([
        calcProfileService.getProfile(partnershipId, year).catch((err: unknown) => {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 404 || status === undefined) {
            return { id: '', partnershipId, taxYear: year, name: '', status: '', items: [] } as CalcProfileData;
          }
          throw err;
        }),
        standardCalcService.getAll().catch(() => [] as StandardCalculation[]),
      ]);
      setProfile(data);
      setAllSCs(scs);
    } catch {
      message.error('Failed to load calculation profile');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPartnership) {
      fetchProfile(selectedPartnership.id, taxYear);
    } else {
      setProfile(null);
    }
  }, [selectedPartnership, taxYear, fetchProfile]);

  const refresh = () => {
    if (selectedPartnership) fetchProfile(selectedPartnership.id, taxYear);
  };

  const handleDeleteItem = async (item: CalcProfileItem) => {
    try {
      await calcProfileService.deleteItem(item.id);
      message.success(`${item.standardCalculationName} removed from profile`);
      refresh();
    } catch {
      message.error('Failed to remove calculation');
    }
  };

  return (
    <div className={styles.pageWrap}>
      {!selectedPartnership ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><BarChartOutlined style={{ fontSize: 32, color: '#ADADAD' }} /></div>
          <div className={styles.emptyTitle}>Select a client and partnership</div>
          <div className={styles.emptyText}>to view the calculation profile</div>
        </div>
      ) : (
        <>
          <div className={styles.pageHead}>
            <div className={styles.titleBlock}>
              <h1 className={styles.pageTitle}>Calculation Profile</h1>
              <p className={styles.pageSub}>
                {selectedPartnership.name} · The calculations that apply to this partnership.
              </p>
            </div>
            <div className={styles.headActions}>
              <button className={styles.btnOutline} onClick={() => message.info('Copy profile — coming soon')}>
                Copy Profile
              </button>
              <button className={styles.btnPrimary} onClick={() => setShowAdd(true)}>
                <PlusOutlined /> Add Calculation
              </button>
            </div>
          </div>

          {autoAddedBanner && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
              background: 'rgba(26,39,68,0.82)', borderLeft: '3px solid #F26522', borderRadius: 6,
              padding: '10px 14px', margin: '0 24px 16px', fontFamily: 'Inter, sans-serif',
            }}>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>
                {autoAddedBanner.length === 1
                  ? `${autoAddedBanner[0]} was automatically added as a dependency.`
                  : `${autoAddedBanner.join(', ')} were automatically added as dependencies.`}
              </div>
              <button
                onClick={() => setAutoAddedBanner(null)}
                style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.45)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                aria-label="Dismiss"
              >×</button>
            </div>
          )}

          <div className={styles.tabsWrap}>
            <div className={styles.subTabRow}>
              <button
                className={activeTab === 'profile' ? styles.subTabActive : styles.subTab}
                onClick={() => setActiveTab('profile')}
              >
                Calculation Profile
              </button>
              <button
                className={activeTab === 'sequencing' ? styles.subTabActive : styles.subTab}
                onClick={() => setActiveTab('sequencing')}
              >
                Sequencing
              </button>
            </div>
          </div>

          {loading ? (
            <div className={styles.loadingState}><Spin /></div>
          ) : activeTab === 'profile' ? (
            <div className={styles.tableWrap}>
              <CalcProfileTab
                profile={profile}
                allSCs={allSCs}
                onDeleteItem={handleDeleteItem}
                onAssignTemplate={item => setAssignItem(item)}
              />
            </div>
          ) : (
            <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              <SequencingTab
                profile={profile}
                allSCs={allSCs}
              />
            </div>
          )}
        </>
      )}

      {showAdd && selectedPartnership && (
        <AddCalcModal
          profile={profile}
          partnershipId={selectedPartnership.id}
          taxYear={taxYear}
          onClose={() => setShowAdd(false)}
          onSaved={(autoAddedNames) => {
            setShowAdd(false);
            refresh();
            setAutoAddedBanner(autoAddedNames.length > 0 ? autoAddedNames : null);
          }}
        />
      )}

      {assignItem && selectedPartnership && (
        <AssignTemplateModal
          item={assignItem}
          partnershipName={selectedPartnership.name}
          onClose={() => setAssignItem(null)}
          onSaved={() => { setAssignItem(null); refresh(); }}
        />
      )}
    </div>
  );
};
